import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Hidden } from '@material-ui/core';
import clsx from 'clsx';

import { openModal } from 'features/utilsSlice';
import { useDispatch } from 'react-redux';
import useProfileOwner from 'hooks/useProfileOwner';
import InfluencerNetworks from './InfluencerNetworks';
import WorkingStats from './WorkingStats';
import ProfileCover from 'components/profile/ProfileCover';
import ProfileInfo from 'components/profile/ProfileInfo';
import CenteredDialog from 'components/CenteredDialog';
import ConfirmationDialog from 'components/ConfirmationDialog';
import BottomButtons from 'components/BottomButtons';
import styles from 'styles/components/profile/Profile.module.scss';

const minHeight = { minHeight: 'auto' };

const removeDuplicates = array =>
  array.filter((el, index) => index === array.findIndex(item => item.id === el.id));

const ProfileInDialog = ({
  profile,
  setProfile,
  acceptApplication,
  rejectApplication,
  handleDialogClose,
  isLoadingActions,
  handleInvite,
  hasActions,
  showCartIcon,
  fromMatches,
  hideInviteButton,
}) => {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { working_stats } = profile;
  const profileOwner = useProfileOwner(profile);
  const dispatch = useDispatch();

  const inventories = profileOwner
    ? profile.inventories
    : profile.inventories.filter(inventory => inventory.active);

  const influencerNetworks = inventories.map(inventory => inventory.influencer_network);

  const uniqInfluencerNetworks = removeDuplicates(influencerNetworks);

  return (
    <div
      className={clsx(styles.profileContainer, styles.negativeMargin, {
        [styles.height]: hasActions,
      })}
    >
      <ProfileCover
        setProfile={setProfile}
        profile={profile}
        showCartIcon={showCartIcon}
        isDialog
      />

      <ProfileInfo
        setProfile={setProfile}
        profile={profile}
        handleInvite={handleInvite}
        fromMatches={fromMatches}
        hideInviteButton={hideInviteButton}
        isDialog
      />

      {uniqInfluencerNetworks.length > 0 && (
        <InfluencerNetworks
          profile={profile}
          influencerNetworks={uniqInfluencerNetworks}
          isDialog
        />
      )}

      <Grid container className={clsx(styles.filterContainer, styles.noSpacing)} style={minHeight}>
        <WorkingStats working_stats={working_stats} />

        {hasActions && (
          <BottomButtons
            text="Reject"
            textContained="Accept"
            handleClick={() => {
              dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
              setOpenConfirmation(true);
            }}
            handleClose={() => {
              dispatch(
                openModal({
                  name: 'Confirmation',
                  data: {
                    action: 'reject',
                    rejectTitle: `Reject offer from "${profile.display_name}"`,
                  },
                })
              );
              setOpenConfirmation(true);
            }}
            fixed
          />
        )}
      </Grid>

      {openConfirmation && (
        <ConfirmationDialog
          title={`Accept offer from "${profile.display_name}"`}
          handleApprove={acceptApplication}
          handleReject={() => dispatch(openModal({ name: 'Reject Influencer' }))}
          disabled={isLoadingActions?.accept}
        />
      )}

      <CenteredDialog
        modalName="Reject Influencer"
        title={`It's a good idea to leave the "${profile.display_name}" a rejection reason message.`}
        placeholder="Message..."
        handleSubmit={rejectApplication}
        handleDialogClose={handleDialogClose}
        isLoading={isLoadingActions?.reject}
        alwaysEnabled
      />
    </div>
  );
};

ProfileInDialog.propTypes = {
  setProfile: PropTypes.func.isRequired,
  profile: PropTypes.object,
  acceptApplication: PropTypes.func,
  rejectApplication: PropTypes.func,
  handleDialogClose: PropTypes.func,
  isLoadingActions: PropTypes.object,
  hasActions: PropTypes.bool,
  showCartIcon: PropTypes.bool,
  hideInviteButton: PropTypes.bool,
  handleInvite: PropTypes.func,
  fromMatches: PropTypes.bool,
};

ProfileInDialog.defaultProps = {
  profile: {},
  acceptApplication: () => {},
  rejectApplication: () => {},
  handleDialogClose: () => {},
  isLoadingActions: {},
  hasActions: false,
  showCartIcon: false,
  hideInviteButton: false,
  fromMatches: false,
  handleInvite: () => {},
};

export default ProfileInDialog;
